// import * as THREE from 'three';
// import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import anime from 'animejs/lib/anime.es.js';

const THREE = (window.THREE = require("three"));
require("three/examples/js/loaders/GLTFLoader");
const PCDLoader = require("./PCDLoader");

const PATH_PREFIX = process.env.PATH_PREFIX || "http://localhost:1234/"

const pointFiles = ["a0000001", "a0001000", "a0003000", "a0006000", "a0014000", "d0029000", "d0086000", "d0171000", "a0857000", "a1714000", "a3429000"]

function lerp(a, b, t) {
    return ((1 - t) * a + t * b);
}

function onTransitionEnd(event) {
    const element = event.target;
    element.parentElement.removeChild(element);
}

function docReady(fn) {
    // see if DOM is already available
    if (document.readyState === "complete" || document.readyState === "interactive") {
        // call on next available tick
        setTimeout(fn, 1);
    } else {
        document.addEventListener("DOMContentLoaded", fn);
    }
}

const pointsLoader = new PCDLoader.PCDLoader();
const gltfLoader = new THREE.GLTFLoader();

export class Application {

    constructor() {
        this.currentSeekPosition = 0;
        this.targetSeekPosition = 0;
        this.renderLoopRunning = false;
        this.init()
    }

    init() {
        try {
            const delayPerFile = 1 / pointFiles.length;

            this.files = pointFiles.map((file, index) => {
                return {
                    fileName: file,
                    startAnimationAt: delayPerFile * index
                }
            });

            this.scrollListener = () => this.onScroll();

            Promise.all([this.loadGltf(), this.loadPoints(), this.loadFont(), this.setupRenderer()])
                .then(() => {
                    this.setupScene();
                    this.setupLights();
                    this.scene.add(this.gltf.scene);
                    this.scene.traverse(function (obj) {
                        if (obj.name.startsWith("Person") || obj.name === 'Plane') {
                            obj.castShadow = true
                            obj.receiveShadow = true
                        }
                    })
                    this.cameras = this.gltf.cameras;

                    this.setupCamera();
                    this.setupResize();
                    this.addCircle();
                    this.addPoints();
                    this.addText();
                    this.initTimeline();
                    this.render();

                    this.scene.traverse(function (obj) {
                        obj.matrixAutoUpdate = false;
                    })

                    this.setupObserver();
                    this.removeLoadingScreen();
                })
                .catch((error) => {
                    this.onError(error)
                })
        } catch (error) {
            this.onError(error)
        }
    }

    onError(error) {
        console.trace(error);
        if (this.canvasContainer && this.canvasContainer.parentElement) this.canvasContainer.parentElement.removeChild(this.canvasContainer);
        if (this.scrollListener) window.removeEventListener("scroll", this.scrollListener);
        if (this.observer) this.observer.unobserve(this.blenderContainer);
        this.scene = null;
        this.destroyed = true;
        this.removeLoadingScreen();
        Array.from(this.backgroundContainer.querySelectorAll('.background')).forEach(bg => {
            bg.style.display = 'block';
        })
    }

    getTime(percent) {
        return Math.round(this.duration * percent);
    }

    getSlideTime(slideNumber) {
        return this.getTime(1 / this.numberOfSlides * slideNumber);
    }

    setupObserver() {
        const callback = (events) => {
            if (events[0].isIntersecting) {
                this.setupScroll()
            } else {
                window.removeEventListener("scroll", this.scrollListener)
            }
        }
        this.observer = new IntersectionObserver(callback, {
            threshold: [0]
        });
        this.observer.observe(this.blenderContainer);
    }

    setupScroll() {
        window.addEventListener("scroll", this.scrollListener, {passive: true});
    }

    onScroll() {
        let targetSeekPositionPercent = this.getScrollRatio();
        // avoid negative currentTime
        targetSeekPositionPercent = Math.max(targetSeekPositionPercent, 0);
        // avoid overstepping currentTime
        targetSeekPositionPercent = Math.min(targetSeekPositionPercent, 1);
        this.targetSeekPosition = Math.round(targetSeekPositionPercent * this.duration);
        if (!this.renderLoopRunning) {
            this.renderLoop();
        }
    }

    setupResize() {
        if (window.szerLongreadServices) {
            const viewportService = window.szerLongreadServices.viewportService;
            viewportService.addObserver(viewportService.END_EVENT, () => this.onWindowResize());
        } else {
            window.addEventListener("resize", () => this.onWindowResize(), {passive: true});
        }
        this.onWindowResize();
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight - 55); // subtract header height
        this.contentOffsetTop = window.scrollY + this.blenderContainer.getBoundingClientRect().top;
        this.contentHeight = this.blenderContainer.offsetHeight;
    }

    getScrollRatio() {
        const viewportHeight = window.szerLongreadServices ? window.szerLongreadServices.viewportService.viewportSize.height : window.innerHeight;
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        const ratio = (scrollTop - this.contentOffsetTop) / (this.contentHeight - viewportHeight);
        return ratio;
    }

    loadPoints() {
        return Promise.all(this.files.map((file) => {
            return new Promise((res, rej) => {
                pointsLoader.load(`${PATH_PREFIX}${file.fileName}.bin.pcd`, (points) => {
                    points.material.size *= 6;
                    points.material.opacity = 0;
                    points.material.transparent = true;
                    points.material.alphaTest = .1;
                    points.up.set(0, 0, 1);
                    points.position.set(0, 0, 0);

                    file.points = points;
                    res()
                }, () => {
                }, (error) => {
                    console.error(`error loading file ${file.fileName}`);
                    rej(error);
                })
            })
        }))
    }

    addPoints() {
        this.files.forEach((file) => this.scene.add(file.points));
    }

    loadGltf() {
        return new Promise((res, rej) => {
            gltfLoader.load(`${PATH_PREFIX}raum.gltf`,
                gltf => {
                    this.gltf = gltf;
                    res();
                },
                () => {
                },
                rej
            );
        })
    }

    addCircle() {
        const geometry = new THREE.CircleGeometry(1.5, 32, 0, Math.PI);
        const material = new THREE.MeshBasicMaterial({
            color: 0x232830,
            // wireframe: true,
            opacity: 0,
            alphaTest: .1,
            transparent: true
        });
        this.circle = new THREE.Mesh(geometry, material);
        this.circle.position.set(3.85, 0.35, 0);

        this.scene.add(this.circle);
    }

    loadFont() {
        const loader = new THREE.FontLoader();
        return new Promise((res, rej) => {
            loader.load(`${PATH_PREFIX}Work-Sans-Medium_Regular.json`, (font) => {
                const geometryDistance = new THREE.TextGeometry('1,5m', {
                    font: font,
                    size: 0.12,
                    height: 0.001,
                });
                const geometryRoomLength = new THREE.TextGeometry('7,5 Meter', {
                    font: font,
                    size: 0.2,
                    height: 0.001,
                });
                const geometryRoomWidth = new THREE.TextGeometry('8 Meter', {
                    font: font,
                    size: 0.2,
                    height: 0.001,
                });
                const material = new THREE.MeshBasicMaterial({
                    color: 0xd3d3d3,
                    opacity: 0,
                    alphaTest: .1,
                    transparent: true
                });
                const materialRoom = new THREE.MeshBasicMaterial({
                    color: 0x202020,
                    opacity: 0,
                    alphaTest: .1,
                    transparent: true
                });
                this.textDistance = new THREE.Mesh(geometryDistance, material);
                this.textDistance.position.set(3.6, 1.8, 0)
                this.textDistance.rotation.set(0, 0, -1.55)

                this.textRoomLength = new THREE.Mesh(geometryRoomLength, materialRoom);
                this.textRoomLength.position.set(0.5, -0.3, 0)
                this.textRoomLength.rotation.set(0, 0, 0)

                this.textRoomWidth = new THREE.Mesh(geometryRoomWidth, materialRoom);
                this.textRoomWidth.position.set(-0.3, 1.5, 0)
                this.textRoomWidth.rotation.set(0, 0, -1.55)
                res();
            }, () => {
            }, rej)
        });
    }

    addText() {
        this.scene.add(this.textDistance);
        this.scene.add(this.textRoomLength);
        this.scene.add(this.textRoomWidth);
    }

    goToCamera(cameraIndex, start, duration) {
        this.timeline.add({
            targets: this.camera.position,
            x: this.cameras[cameraIndex].position.x,
            y: this.cameras[cameraIndex].position.y,
            z: this.cameras[cameraIndex].position.z,
            duration
        }, start)
        this.timeline.add({
            targets: this.camera.rotation,
            x: this.cameras[cameraIndex].rotation.x,
            y: this.cameras[cameraIndex].rotation.y,
            z: this.cameras[cameraIndex].rotation.z,
            duration
        }, start)
    }

    initTimeline() {
        this.timeline = anime.timeline({
            autoplay: false,
            easing: 'easeOutSine'
        });

        // Slide 1
        // Licht an
        // Raum-Maße einblenden
        this.timeline.add({
            targets: this.pointLight,
            intensity: 80,
            duration: 200
        }, this.getTime(0))
        this.timeline.add({
            targets: this.pointLight2,
            intensity: 50,
            duration: 200
        }, this.getTime(0))
        this.timeline.add({
            targets: this.textRoomLength.material,
            opacity: 1,
            duration: 200
        }, this.getSlideTime(0))
        this.timeline.add({
            targets: this.textRoomWidth.material,
            opacity: 1,
            duration: 200
        }, this.getSlideTime(0))

        // Slide 2
        // Kamerafahrt zu Kamera 2
        // Raum-Maße ausblenden
        this.goToCamera(2, this.getSlideTime(1), this.getSlideTime(1))
        this.timeline.add({
            targets: this.textRoomLength.material,
            opacity: 0,
            duration: 200
        }, this.getSlideTime(1))
        this.timeline.add({
            targets: this.textRoomWidth.material,
            opacity: 0,
            duration: 200
        }, this.getSlideTime(1))

        // Slide 4
        // Kamerafahrt zu Kamera 3
        this.goToCamera(3, this.getSlideTime(2), this.getSlideTime(2))

        // Slide 6
        // Abstand einblenden
        this.timeline.add({
            targets: this.textDistance.material,
            opacity: 1,
            duration: 200
        }, this.getSlideTime(5))
        this.timeline.add({
            targets: this.circle.material,
            opacity: 1,
            duration: 200
        }, this.getSlideTime(5))

        // Slide 8
        // Kamerafahrt zu Kamera 1
        this.goToCamera(1, this.getSlideTime(7), this.getSlideTime(1))
        // Licht aus
        // Abstand ausblenden
        this.timeline.add({
            targets: this.pointLight,
            intensity: 10,
            duration: this.getSlideTime(1)
        }, this.getSlideTime(7))
        this.timeline.add({
            targets: this.pointLight2,
            intensity: 10,
            duration: this.getSlideTime(1)
        }, this.getSlideTime(7))
        this.timeline.add({
            targets: this.textDistance.material,
            opacity: 0,
            duration: this.getSlideTime(1)
        }, this.getSlideTime(7))
        this.timeline.add({
            targets: this.circle.material,
            opacity: 0,
            duration: this.getSlideTime(1)
        }, this.getSlideTime(7))


        // Ab Slide 3
        // Animierte Wolke - Einblenden
        this.timlineForPointFiles(this.files.slice(0, 7), this.getSlideTime(1), this.getSlideTime(2), false)
        this.timlineForPointFiles(this.files.slice(7), this.getSlideTime(7), this.getSlideTime(3), true)

    }

    timlineForPointFiles(files, duration, startAt, keepLast) {
        const timePerFile = Math.round(duration / files.length);
        // const animationDuration = Math.min(timePerFile, this.getSlideTime(.25));
        const animationDuration = timePerFile;
        files.forEach((file, index) => {
            this.timeline.add({
                targets: file.points.material,
                opacity: 1,
                duration: animationDuration,
                easing: 'easeOutCirc'
            }, (timePerFile * index) + startAt)
        })
        // Animierte Wolke - Ausblenden
        files.forEach((file, index) => {
            // letzte Wolke bleibt setehen
            if (keepLast && files.length - index === 1) {
                return
            }
            this.timeline.add({
                targets: file.points.material,
                opacity: 0,
                duration: animationDuration,
                easing: 'easeOutCirc'
            }, (timePerFile * (index + 1)) + startAt)
        })
    }

    setupScene() {
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color("rgb(255, 255, 255)");
        this.scene.autoUpdate = true;

        // THREE.Object3D.DefaultUp = new THREE.Vector3(0, 0, 1); // needed to point z axis up
        // const axesHelper = new THREE.AxesHelper(5);
        // this.scene.add(axesHelper);
    }

    setupRenderer() {
        return new Promise((res, rej) => {
            docReady(() => {
                try {
                    const canvasContainer = document.createElement("div");
                    canvasContainer.classList.add("canvas-container", "er-viewport--height");
                    this.canvasContainer = canvasContainer;

                    // add loading screen to DOM
                    this.blenderContainer = document.querySelector(window.canvasContainerSelector);
                    if (this.blenderContainer) {
                        this.backgroundContainer = this.blenderContainer.querySelector(".background-container");
                        this.backgroundContainer.appendChild(canvasContainer);
                    } else {
                        this.blenderContainer = document.querySelector('.aerosols-container');
                        this.blenderContainer.appendChild(canvasContainer);
                    }

                    const loadingScreen = document.createElement("div");
                    loadingScreen.id = "loading-screen";

                    const loadingAnimation = document.createElement("div");
                    loadingAnimation.id = "loader";
                    loadingScreen.appendChild(loadingAnimation);

                    if (this.backgroundContainer) {
                        this.backgroundContainer.appendChild(loadingScreen);
                        Array.from(this.backgroundContainer.querySelectorAll('.background')).forEach(bg => {
                            bg.style.display = 'none';
                        })
                    }

                    this.numberOfSlides = this.blenderContainer.querySelectorAll(".slide-container").length;
                    this.duration = Math.round(this.blenderContainer.getBoundingClientRect().height);


                    this.renderer = new THREE.WebGLRenderer({
                        failIfMajorPerformanceCaveat: true
                    });
                    this.renderer.outputEncoding = THREE.sRGBEncoding;
                    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));

                    const canvasContainerBounds = canvasContainer.getBoundingClientRect()
                    const canvasContainerHeight = canvasContainerBounds.height;
                    this.renderer.setSize(window.innerWidth, canvasContainerHeight);

                    this.renderer.shadowMap.enabled = true;

                    this.renderer.physicallyCorrectLights = true;
                    canvasContainer.appendChild(this.renderer.domElement);

                    res();
                } catch (error) {
                    rej(error);
                }
            })
        })
    }

    setupCamera() {
        const aspect = window.innerWidth / window.innerHeight;
        const near = 0.1;
        const far = 100;
        let fov = 40;
        const position = this.cameras[1].position


        this.camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
        this.camera.position.set(position.x, position.y, position.z)
        this.camera.up.set(0, 0, 1); // needed to point z axis up

        this.camera.lookAt(6, 4, 0);
    }

    setupLights() {
        this.pointLight = new THREE.PointLight(0xffffff, 10, 0, 2);
        this.pointLight.position.set(3.5, 2.5, 4);
        this.pointLight.castShadow = true;
        this.scene.add(this.pointLight);

        this.pointLight2 = new THREE.PointLight(0xffffff, 10, 0, 2);
        this.pointLight2.name = "PointLightNoShadows";
        this.pointLight2.position.set(1, 6, 4);
        this.pointLight2.castShadow = false;
        this.scene.add(this.pointLight2);
    }

    removeLoadingScreen() {
        const loadingScreen = document.getElementById("loading-screen");

        if (loadingScreen) {
            loadingScreen.classList.add('fade-out');
            loadingScreen.addEventListener('transitionend', onTransitionEnd);
        }
    }

    render() {
        this.timeline.seek(this.currentSeekPosition);
        this.renderer.render(this.scene, this.camera);
    }

    renderLoop() {
        try {
            this.renderLoopRunning = true;
            this.currentSeekPosition = lerp(this.currentSeekPosition, this.targetSeekPosition, .14);
            if (Math.abs(this.targetSeekPosition - this.currentSeekPosition) >= 1 && !this.destroyed) {
                this.render();
                requestAnimationFrame(() => {
                    this.renderLoop();
                })
            } else {
                this.renderLoopRunning = false;
            }
        } catch (error) {
            this.onError(error);
        }
    }
}

new Application();

